/**
 * Status / priority / resolution-note editor for a ticket.
 *
 * One commit point. The three fields are a local draft that only reaches
 * the API when the operator presses Save, which fixes two things the old
 * card got wrong: picking "Resolved" from a dropdown closed the ticket
 * instantly with no confirmation, and the resolution note had no save of
 * its own — it only persisted if you happened to change the status too.
 *
 * Closing a ticket is deliberately *not* in the dropdown. Resolve and
 * Reject are explicit buttons that require a note and confirm first, and
 * they are hidden from users who cannot close tickets (previously the
 * option was offered and the server silently refused).
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, RotateCcw, Save, Ticket, Undo2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Label } from '@/components/ui/Label';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  usePatchCrmTicket,
  type CrmTicket,
  type CrmTicketPriority,
  type CrmTicketStatus,
  type PatchTicketBody,
} from '@/api/bi/crm';
import {
  OPEN_TICKET_STATUSES,
  TICKET_PRIORITY_LABEL,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  isOpenStatus,
} from '@/lib/crmStatus';
import { apiErrorMessage } from '@/lib/apiError';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import type { TicketCapabilities } from '@/features/crm/useTicketCapabilities';

const PRIORITY_VALUES: CrmTicketPriority[] = ['low', 'normal', 'high', 'critical'];

interface Draft {
  status: CrmTicketStatus;
  priority: CrmTicketPriority;
  note: string;
}

function fromTicket(ticket: CrmTicket): Draft {
  return {
    status: ticket.status,
    priority: ticket.priority,
    note: ticket.resolution_note ?? '',
  };
}

type Closing = 'resolved' | 'rejected';

export function TicketStateCard({
  ticket,
  capabilities,
}: {
  ticket: CrmTicket;
  capabilities: TicketCapabilities;
}) {
  const patch = usePatchCrmTicket(ticket.id);
  const [draft, setDraft] = useState<Draft>(() => fromTicket(ticket));
  const [noteError, setNoteError] = useState<string | null>(null);
  const [closing, setClosing] = useState<Closing | null>(null);

  const server = useMemo(() => fromTicket(ticket), [ticket]);
  const dirty =
    draft.status !== server.status ||
    draft.priority !== server.priority ||
    draft.note !== server.note;

  // Re-seed from the server only while the operator has nothing in flight,
  // so a background refetch (or someone else's edit) cannot wipe a draft.
  useEffect(() => {
    if (dirty) return;
    setDraft(server);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, server.status, server.priority, server.note]);

  const isClosed = !isOpenStatus(ticket.status);
  const canEdit = capabilities.canEdit;
  const canClose = capabilities.canClose;

  /** Closed statuses stay selectable only if the ticket is already there. */
  const statusOptions = useMemo<CrmTicketStatus[]>(() => {
    const options = [...OPEN_TICKET_STATUSES];
    if (!isOpenStatus(draft.status) && !options.includes(draft.status)) {
      options.push(draft.status);
    }
    return options;
  }, [draft.status]);

  async function commit(body: PatchTicketBody, success: string, failure: string) {
    try {
      await patch.mutateAsync(body);
      notify.success(success);
      return true;
    } catch (err) {
      notify.error(apiErrorMessage(err, failure));
      return false;
    }
  }

  async function handleSave() {
    const body: PatchTicketBody = {};
    if (draft.status !== server.status) body.status = draft.status;
    if (draft.priority !== server.priority) body.priority = draft.priority;
    if (draft.note !== server.note) body.resolution_note = draft.note;
    if (Object.keys(body).length === 0) return;
    await commit(body, 'Ticket updated.', 'Could not update the ticket.');
  }

  async function handleClose(decision: Closing) {
    const ok = await commit(
      { status: decision, resolution_note: draft.note.trim() },
      decision === 'resolved' ? 'Ticket resolved.' : 'Ticket rejected.',
      decision === 'resolved' ? 'Could not resolve the ticket.' : 'Could not reject the ticket.',
    );
    if (!ok) return;
    // Closing bypasses the dropdown, so pull the draft along with it —
    // otherwise the card would sit there claiming unsaved changes.
    setDraft((d) => ({ ...d, status: decision, note: d.note.trim() }));
    setClosing(null);
  }

  function requestClose(decision: Closing) {
    if (!draft.note.trim()) {
      setNoteError(
        decision === 'resolved'
          ? 'Say what was done before resolving — this note is the case record.'
          : 'Say why this ticket is being rejected.',
      );
      return;
    }
    setNoteError(null);
    setClosing(decision);
  }

  async function handleReopen() {
    const ok = await commit(
      { status: 'investigating' },
      'Ticket reopened.',
      'Could not reopen the ticket.',
    );
    if (ok) setDraft((d) => ({ ...d, status: 'investigating' }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Ticket className="h-4 w-4" /> Status
        </CardTitle>
        <div className="inline-flex items-center gap-2">
          {dirty ? <Badge tone="warning">Unsaved</Badge> : null}
          <Badge tone={TICKET_STATUS_TONE[ticket.status]}>
            {TICKET_STATUS_LABEL[ticket.status]}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor={`ticket-status-${ticket.id}`} className="text-[11px] uppercase tracking-wide">
              Status
            </Label>
            <Select
              value={draft.status}
              onValueChange={(v) => setDraft((d) => ({ ...d, status: v as CrmTicketStatus }))}
              disabled={!canEdit || patch.isPending}
            >
              <SelectTrigger id={`ticket-status-${ticket.id}`} className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {TICKET_STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`ticket-priority-${ticket.id}`} className="text-[11px] uppercase tracking-wide">
              Priority
            </Label>
            <Select
              value={draft.priority}
              onValueChange={(v) => setDraft((d) => ({ ...d, priority: v as CrmTicketPriority }))}
              disabled={!canEdit || patch.isPending}
            >
              <SelectTrigger id={`ticket-priority-${ticket.id}`} className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_VALUES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {TICKET_PRIORITY_LABEL[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ticket-note-${ticket.id}`} className="text-[11px] uppercase tracking-wide">
            Resolution note
          </Label>
          <textarea
            id={`ticket-note-${ticket.id}`}
            value={draft.note}
            onChange={(e) => {
              setDraft((d) => ({ ...d, note: e.target.value }));
              if (noteError) setNoteError(null);
            }}
            rows={3}
            disabled={!canEdit || patch.isPending}
            aria-invalid={noteError ? true : undefined}
            aria-describedby={noteError ? `ticket-note-error-${ticket.id}` : undefined}
            placeholder="What was done, who confirmed, refund hand-off id, etc."
            className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs disabled:opacity-60"
          />
          {noteError ? (
            <p id={`ticket-note-error-${ticket.id}`} className="text-[11px] text-danger">
              {noteError}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Required when resolving or rejecting. Saved on its own — no need to touch the status.
            </p>
          )}
        </div>

        {!canEdit ? (
          <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            {capabilities.reason.edit}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft(server);
              setNoteError(null);
            }}
            disabled={!dirty || patch.isPending}
            className="gap-1"
          >
            <Undo2 className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canEdit || !dirty || patch.isPending}
            className="gap-1"
          >
            <Save className="h-3.5 w-3.5" />
            {patch.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          {isClosed ? (
            <>
              <p className="text-[11px] text-muted-foreground">
                {ticket.status === 'resolved' ? 'Resolved' : 'Rejected'}
                {ticket.resolved_by ? ` by ${ticket.resolved_by.name}` : ''}
                {ticket.resolved_at ? ` · ${fmt.ago(ticket.resolved_at)}` : ''}
              </p>
              <GuardedAction reason={canClose ? null : capabilities.reason.close}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReopen}
                  disabled={!canClose || patch.isPending}
                  className="gap-1"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reopen
                </Button>
              </GuardedAction>
            </>
          ) : (
            <>
              <p className="text-[11px] text-muted-foreground">
                {canClose
                  ? 'Closing asks for confirmation.'
                  : capabilities.reason.close}
              </p>
              <div className="flex items-center gap-2">
                <GuardedAction reason={canClose ? null : capabilities.reason.close}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => requestClose('rejected')}
                    disabled={!canClose || patch.isPending}
                    className="gap-1"
                  >
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </Button>
                </GuardedAction>
                <GuardedAction reason={canClose ? null : capabilities.reason.close}>
                  <Button
                    size="sm"
                    onClick={() => requestClose('resolved')}
                    disabled={!canClose || patch.isPending}
                    className="gap-1"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
                  </Button>
                </GuardedAction>
              </div>
            </>
          )}
        </div>
      </CardBody>

      <ConfirmDialog
        open={closing !== null}
        onOpenChange={(next) => (next ? null : setClosing(null))}
        title={closing === 'rejected' ? 'Reject this ticket?' : 'Resolve this ticket?'}
        description={
          closing === 'rejected'
            ? 'Rejecting closes the ticket without a fix. Assignees are notified.'
            : 'Resolving closes the ticket and stops its SLA clocks. Assignees are notified.'
        }
        details={
          <div className="space-y-1">
            <p className="font-medium">
              #{ticket.id} {ticket.summary || TICKET_STATUS_LABEL[ticket.status]}
            </p>
            <p className="whitespace-pre-wrap text-muted-foreground">{draft.note.trim()}</p>
          </div>
        }
        confirmLabel={closing === 'rejected' ? 'Reject ticket' : 'Resolve ticket'}
        tone={closing === 'rejected' ? 'destructive' : 'primary'}
        isPending={patch.isPending}
        onConfirm={() => (closing ? handleClose(closing) : undefined)}
      />
    </Card>
  );
}

/**
 * Tooltip wrapper for a disabled action. A disabled `<button>` swallows
 * pointer events, so the trigger has to be the surrounding span.
 */
export function GuardedAction({
  reason,
  children,
}: {
  reason: string | null;
  children: React.ReactNode;
}) {
  if (!reason) return <>{children}</>;
  return (
    <Tooltip content={reason}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}
